import styled from 'styled-components';
import { ProductDTO } from 'src/models/pim';
import { useTranslateItem } from 'src/hooks/directus/useTranslateItem';
import { ConstraintContainer } from 'src/components/common/ConstraintContainer';
import { Markdown } from 'vendor/mods/markdown/components/Markdown';
import { FC } from 'react';
import { Heading } from 'src/components/common/Heading';
import { useTitlesDictionary } from 'src/modules/products/hooks/useTitlesDictionary';
import { media } from 'src/styles/designs';
import { ProductPageShowcaseAttribute } from 'src/modules/products/components/ProductPage/ProductPageShowcase/ProductPageShowcaseAttribute';
import { DictionaryTitlesCode } from 'src/models/pim/dictionaries';
import { ProductAttributeCode } from 'src/models/pim/attributes';
import { PimImage } from 'src/components/common/PimImage';
import { useProductAttributes } from 'src/modules/products/hooks/useProductAttributes';
import { head } from 'lodash';

const Section = styled.section`
  padding: 32px 0;
  ${media.w.greaterThanOrEqual.desktop} {
    padding: 56px 0 78px;
  }
`;

const ContentContainer = styled(ConstraintContainer)`
  display: flex;
  flex-direction: column-reverse;

  ${media.w.greaterThanOrEqual.desktop} {
    flex-direction: row;
    gap: 110px;
  }
`;

const ImageContainer = styled.div`
  position: relative;
  width: 100%;
  max-width: 427px;
  height: 404px;
  ${media.w.greaterThanOrEqual.desktop} {
    height: 601px;
  }
`;

const AttributesContainer = styled.div`
  width: 100%;
  max-width: 440px;
`;

const Attribute = styled(ProductPageShowcaseAttribute)`
  border: none;
  & > span {
    font-weight: 700;
    font-size: 18px;
  }
  & > p {
    font-size: 16px;
  }
  ${media.w.lessThan.desktop} {
    padding: 12px 0;
  }
`;

export const ProductPageFeaturesSection: FC<ProductDTO> = (product) => {
  const translateFn = useTranslateItem();

  const { getProductAttribute } = useProductAttributes(product.attributes);

  const image = head(getProductAttribute(ProductAttributeCode.FeaturesImage));

  const [title] = useTitlesDictionary(DictionaryTitlesCode.ProductFeatures);

  return (
    <Section>
      <ContentContainer>
        <AttributesContainer>
          {title && (
            <Heading>
              <Markdown>{translateFn(title).value}</Markdown>
            </Heading>
          )}

          <Attribute
            product={product}
            labelCode={DictionaryTitlesCode.ExtrusionForce}
            descriptionCode={ProductAttributeCode.ExtrusionForce}
          />
          <Attribute
            product={product}
            labelCode={DictionaryTitlesCode.Duration}
            descriptionCode={ProductAttributeCode.Duration}
          />
          <Attribute
            product={product}
            labelCode={DictionaryTitlesCode.InjectionPlace}
            descriptionCode={ProductAttributeCode.InjectionPlace}
          />
          <Attribute
            product={product}
            labelCode={DictionaryTitlesCode.InjectionTool}
            descriptionCode={ProductAttributeCode.InjectionTool}
          />
        </AttributesContainer>

        {image && (
          <ImageContainer>
            <PimImage
              src={image.directus_files_id.id}
              layout="fill"
              objectFit="cover"
              sizes="100vw"
            />
          </ImageContainer>
        )}
      </ContentContainer>
    </Section>
  );
};
